import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MdAdd, MdCheck, MdContentCopy, MdDownload, MdSearch } from 'react-icons/md';
import api from '../utils/api';
import IssueDrawer from './IssueDrawer';
import CertificatePreview, { formatDate } from './CertificatePreview';
import './ListCertificates.css';

interface Certificate {
    id: number;
    first_name: string;
    last_name: string;
    organization: string;
    course_name: string;
    issue_date: string;
    certificate_hash: string;
}

type Notice = { id: number; message: string; type: 'success' | 'error' | 'info' };

const initials = (c: Certificate) => `${c.first_name?.[0] ?? ''}${c.last_name?.[0] ?? ''}`.toUpperCase();
const fullHash = (hash: string) => `${hash}_certificate`;

const ListCertificates = ({ openIssue = false }: { openIssue?: boolean }) => {
    const [certificates, setCertificates] = useState<Certificate[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [query, setQuery] = useState('');
    const [activeId, setActiveId] = useState<number | null>(null);
    const [selectedHashes, setSelectedHashes] = useState<string[]>([]);
    const [notifications, setNotifications] = useState<Notice[]>([]);
    const [isDownloading, setIsDownloading] = useState<string | null>(null);
    const [drawerOpen, setDrawerOpen] = useState(openIssue);
    const [copied, setCopied] = useState<string | null>(null);
    const [missing, setMissing] = useState<string[]>([]);
    const knownIds = useRef<Set<number>>(new Set());

    const showNotification = (message: string, type: Notice['type'] = 'info') => {
        const id = Date.now();
        setNotifications(prev => [...prev, { id, message, type }]);
        setTimeout(() => setNotifications(prev => prev.filter(n => n.id !== id)), 5000);
    };

    const getBlobError = async (blob: Blob) => {
        try {
            const json = JSON.parse(await blob.text());
            return json.message || 'Server error';
        } catch (e) {
            return 'Failed to download file';
        }
    };

    const saveBlob = (blob: Blob, filename: string) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
            link.remove();
            window.URL.revokeObjectURL(url);
        }, 1000);
    };

    const fetchCertificates = useCallback(async (focusNew = false) => {
        try {
            const response = await api.get('/v1/vitrine/certificates/list');
            const next: Certificate[] = response.data;
            if (focusNew) {
                const fresh = next.filter(c => !knownIds.current.has(c.id));
                if (fresh.length) setActiveId(Math.max(...fresh.map(c => c.id)));
            }
            knownIds.current = new Set(next.map(c => c.id));
            setCertificates(next);
        } catch (err: any) {
            showNotification('Could not load certificates archive', 'error');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCertificates();
    }, [fetchCertificates]);

    const handleIssued = () => {
        fetchCertificates(true);
        showNotification('Certificate issued', 'success');
    };

    const handleDownloadCertificate = async (hash: string) => {
        try {
            setIsDownloading(hash);
            const response = await api.post(`/v1/vitrine/certificates/download/${fullHash(hash)}`, {}, { responseType: 'blob' });
            if (response.data.type === 'application/json') {
                showNotification(await getBlobError(response.data), 'error');
                setMissing(prev => [...prev, hash]);
                return;
            }
            setMissing(prev => prev.filter(h => h !== hash));
            saveBlob(new Blob([response.data], { type: 'application/pdf' }), `certificate_${hash}.pdf`);
            showNotification('Download started', 'success');
        } catch (err: any) {
            const msg = err.response?.data instanceof Blob
                ? await getBlobError(err.response.data)
                : (err.response?.data?.message || 'Failed to download certificate');
            if (err.response?.status === 404) setMissing(prev => [...prev, hash]);
            showNotification(msg, 'error');
        } finally {
            setIsDownloading(null);
        }
    };

    const handleDownloadSelected = async () => {
        if (selectedHashes.length === 0) return;
        try {
            setIsDownloading('bulk');
            const response = await api.post('/v1/vitrine/certificates/download', { hashes: selectedHashes }, { responseType: 'blob' });
            if (response.data.type === 'application/json') {
                showNotification(await getBlobError(response.data), 'error');
                return;
            }
            saveBlob(new Blob([response.data], { type: 'application/zip' }), `certificates_${new Date().getTime()}.zip`);
            showNotification(`Downloading ${selectedHashes.length} certificates`, 'success');
        } catch (err: any) {
            const msg = err.response?.data instanceof Blob
                ? await getBlobError(err.response.data)
                : (err.response?.data?.message || 'Failed to download selected');
            showNotification(msg, 'error');
        } finally {
            setIsDownloading(null);
        }
    };

    const copyHash = async (hash: string) => {
        try {
            await navigator.clipboard.writeText(hash);
            setCopied(hash);
            setTimeout(() => setCopied(null), 1500);
        } catch (e) {
            showNotification('Could not copy the hash', 'error');
        }
    };

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return certificates;
        return certificates.filter(c =>
            `${c.first_name} ${c.last_name} ${c.organization} ${c.course_name} ${c.certificate_hash}`.toLowerCase().includes(q));
    }, [certificates, query]);

    const active = certificates.find(c => c.id === activeId) ?? filtered[0] ?? null;
    const activeMissing = !!active && missing.includes(active.certificate_hash);

    const allFilteredSelected = filtered.length > 0 && filtered.every(c => selectedHashes.includes(fullHash(c.certificate_hash)));

    const handleSelectAll = () => {
        const hashes = filtered.map(c => fullHash(c.certificate_hash));
        setSelectedHashes(prev => allFilteredSelected
            ? prev.filter(h => !hashes.includes(h))
            : Array.from(new Set([...prev, ...hashes])));
    };

    const toggleOne = (hash: string) => {
        const key = fullHash(hash);
        setSelectedHashes(prev => prev.includes(key) ? prev.filter(h => h !== key) : [...prev, key]);
    };

    if (isLoading) return <div className="certificates-container">Loading certificates...</div>;

    return (
        <div className="certificates-container">
            <div className="notification-tray">
                {notifications.map(n => (
                    <div key={n.id} className={`notification ${n.type}`}>{n.message}</div>
                ))}
            </div>

            <div className="certificates-header">
                <div>
                    <h1>Certificates</h1>
                    <p className="header-sub">Select a row to preview it.</p>
                </div>
                <div className="header-actions">
                    <span className="pill pill-soft">All {certificates.length}</span>
                    {missing.length > 0 && <span className="pill pill-bad">PDF missing {missing.length}</span>}
                    <button className="btn-solid" onClick={() => setDrawerOpen(true)}>
                        <MdAdd size={18} /> Issue certificate
                    </button>
                </div>
            </div>

            <div className="split">
                <section className="list-card">
                    <div className="list-toolbar">
                        <label className="search-box">
                            <MdSearch size={18} />
                            <input
                                type="search"
                                placeholder="Search name, course, organization or hash"
                                value={query}
                                onChange={e => setQuery(e.target.value)}
                            />
                        </label>
                        <span className="result-count">{filtered.length} of {certificates.length}</span>
                    </div>

                    <div className="table-wrapper">
                        <table className="modern-table">
                            <thead>
                                <tr>
                                    <th className="col-check">
                                        <input type="checkbox" checked={allFilteredSelected} onChange={handleSelectAll} aria-label="Select all" />
                                    </th>
                                    <th>Recipient</th>
                                    <th>Course</th>
                                    <th>Issued</th>
                                    <th>PDF</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map(cert => {
                                    const checked = selectedHashes.includes(fullHash(cert.certificate_hash));
                                    const isActive = active?.id === cert.id;
                                    const isMissing = missing.includes(cert.certificate_hash);
                                    return (
                                        <tr key={cert.id} className={`${isActive ? 'is-active' : ''}${checked ? ' is-checked' : ''}`} onClick={() => setActiveId(cert.id)}>
                                            <td className="col-check" onClick={e => e.stopPropagation()}>
                                                <input type="checkbox" checked={checked} onChange={() => toggleOne(cert.certificate_hash)} aria-label={`Select ${cert.first_name} ${cert.last_name}`} />
                                            </td>
                                            <td>
                                                <button className="recipient" onClick={() => setActiveId(cert.id)} aria-pressed={isActive}>
                                                    <span className="avatar">{initials(cert)}</span>
                                                    <span className="recipient-text">
                                                        <strong dir="auto">{cert.first_name} {cert.last_name}</strong>
                                                        <small dir="auto">{cert.organization}</small>
                                                    </span>
                                                </button>
                                            </td>
                                            <td className="cell-course" dir="auto">{cert.course_name || '—'}</td>
                                            <td className="nowrap">{formatDate(cert.issue_date)}</td>
                                            <td><span className={`pill ${isMissing ? 'pill-bad' : 'pill-ok'}`}>{isMissing ? 'Missing' : 'Ready'}</span></td>
                                        </tr>
                                    );
                                })}
                                {filtered.length === 0 && (
                                    <tr><td colSpan={5} className="empty-row">
                                        {certificates.length === 0 ? 'No certificates yet. Use "Issue certificate" to create the first one.' : 'No certificates match your search.'}
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </section>

                {active && (
                    <aside className="detail-card">
                        <div className="detail-head">
                            <div>
                                <small>Certificate</small>
                                <strong dir="auto">{active.first_name} {active.last_name}</strong>
                            </div>
                            <span className={`pill ${activeMissing ? 'pill-bad' : 'pill-ok'}`}>{activeMissing ? 'Missing' : 'Ready'}</span>
                        </div>

                        <CertificatePreview data={{
                            name: `${active.first_name} ${active.last_name}`,
                            organization: active.organization,
                            course: active.course_name,
                            date: active.issue_date,
                        }} />

                        <div className="hash-box">{active.certificate_hash}</div>

                        {activeMissing && (
                            <div className="inline-alert" role="alert">
                                The PDF file was not found on the server. The record is intact; contact support to regenerate it.
                            </div>
                        )}

                        <div className="detail-actions">
                            <button className="btn-solid" onClick={() => handleDownloadCertificate(active.certificate_hash)} disabled={!!isDownloading}>
                                {isDownloading === active.certificate_hash ? <span className="spinner" /> : <MdDownload size={18} />} Download PDF
                            </button>
                            <button className="btn-outline" onClick={() => copyHash(active.certificate_hash)}>
                                {copied === active.certificate_hash ? <MdCheck size={18} /> : <MdContentCopy size={18} />} Copy hash
                            </button>
                        </div>
                    </aside>
                )}
            </div>

            {selectedHashes.length > 0 && (
                <div className="selection-bar">
                    <strong>{selectedHashes.length} selected</strong>
                    <button className="btn-solid small" onClick={handleDownloadSelected} disabled={!!isDownloading}>
                        <MdDownload size={18} /> {isDownloading === 'bulk' ? 'Preparing ZIP...' : 'Download ZIP'}
                    </button>
                    <button className="btn-link" onClick={() => setSelectedHashes([])}>Clear</button>
                </div>
            )}

            <IssueDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} onIssued={handleIssued} />
        </div>
    );
};

export default ListCertificates;
